import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react'
import type { ReactNode } from 'react'
import { clsx } from 'clsx'
import type { IsolationProps } from '../../Form/Isolation'
import Isolation from '../../Form/Isolation'
import { extractZodSubSchema } from '../../Form/Isolation/extractZodSubSchema'
import * as z from 'zod'
import { isZodSchema } from '../../utils/zod'
import pointer from '../../utils/json-pointer'
import useHandleStatus from '../../Form/Isolation/useHandleStatus'
import SectionContext from '../../Form/Section/SectionContext'
import PushContainerContext from './PushContainerContext'
import IterateItemContext from '../IterateItemContext'
import DataContext from '../../DataContext/Context'
import VisibilityContext from '../../Form/Visibility/VisibilityContext'
import useDataValue from '../../hooks/useDataValue'
import type { IterateEditContainerAllProps as EditContainerProps } from '../EditContainer'
import EditContainer, {
  DoneButton,
  CancelButton,
  ResetButton,
} from '../EditContainer'
import type { ContainerMode } from '../Array'
import IterateArray from '../Array'
import OpenButton from './OpenButton'
import { Flex, FormStatus, HeightAnimation } from '../../../../components'
import type { OnCommit, Path } from '../../types'
import type { SpacingProps } from '../../../../shared/types'
import {
  useArrayLimit,
  useItemPath,
  useSwitchContainerMode,
} from '../hooks'
import Toolbar from '../Toolbar'
import { usePath, useTranslation } from '../../hooks'
import type { JsonObject } from '../../utils'
import { clearedData } from '../../DataContext/Provider'
import { structuredClone } from '../../../../shared/helpers/structuredClone'
import withComponentMarkers from '../../../../shared/helpers/withComponentMarkers'

type OnlyPathRequired = {
  /**
   * The path to the array to add the new item to.
   */
  path: Path

  /**
   * The path to the item in a nested array, to add the new item to.
   */
  itemPath?: Path
}

type OnlyItemPathRequired = {
  /**
   * The path to the array to add the new item to.
   */
  path?: Path

  /**
   * The path to the item in a nested array, to add the new item to.
   */
  itemPath: Path
}

export type IteratePushContainerProps = (
  | OnlyPathRequired
  | OnlyItemPathRequired
) & {
  /**
   * The title of the container.
   */
  title?: ReactNode

  /**
   * If the fields inside the container are required.
   */
  required?: boolean

  /**
   * The index to insert the new item at.
   */
  insertAt?: number

  /**
   * The button to open container.
   */
  openButton?: ReactNode

  /**
   * Define when the "open button" should be shown.
   * Should be a function that returns a boolean.
   */
  showOpenButtonWhen?: (list: unknown[]) => boolean

  /**
   * Prefilled data to be used by fields. The data will be put into this path: `/pushContainerItems/0`. Use `defaultData` when possible.
   */
  data?: unknown | Record<string, unknown>

  /**
   * Prefilled data to be used by fields. The data will be put into this path: `/pushContainerItems/0`.
   */
  defaultData?: unknown | Record<string, unknown>

  /**
   * Provide additional data that will be put into the root of the isolated data context (parallel to `/pushContainerItems/0`).
   */
  isolatedData?: Record<string, unknown>

  /**
   * Prevent the form from being submitted when there are fields with errors inside the PushContainer.
   */
  bubbleValidation?: boolean

  /**
   * Prevents uncommitted changes before the form is submitted. Will display an error message if user tries to submit without committing their changes.
   */
  preventUncommittedChanges?: boolean

  /**
   * Show a button to clear the PushContainer data.
   */
  showResetButton?: boolean

  /**
   * A custom toolbar to be shown below the container.
   */
  toolbar?: ReactNode

  /**
   * Will be called when the user clicks on the "Done" button.
   */
  onCommit?: OnCommit

  /**
   * The container contents.
   */
  children: ReactNode
} & Pick<IsolationProps<JsonObject>, 'dataReference'>

export type IteratePushContainerAllProps = IteratePushContainerProps &
  SpacingProps &
  Omit<EditContainerProps, 'data'>

function PushContainer(props: IteratePushContainerAllProps) {
  const [, forceUpdate] = useReducer(() => ({}), {})
  const outerContext = useContext(DataContext)
  const sectionContext = useContext(SectionContext)
  const { required: requiredInherited } = outerContext
  const { value: outerData } = useDataValue<JsonObject>('//')

  const {
    data: dataProp,
    defaultData: defaultDataProp,
    isolatedData,
    bubbleValidation,
    preventUncommittedChanges,
    dataReference,
    showResetButton,
    path,
    itemPath,
    insertAt,
    title,
    required = requiredInherited,
    children,
    openButton,
    showOpenButtonWhen,
    onCommit,
    ...rest
  } = props

  const { absolutePath } = useItemPath(itemPath)
  const { path: relativePath } = usePath({ path, itemPath })
  const targetPath = useMemo(() => {
    if (absolutePath) {
      return absolutePath
    }

    if (!path) {
      return relativePath
    }

    const sectionPath = sectionContext?.path
    if (
      sectionPath &&
      !path.startsWith('//') &&
      (path === sectionPath || path.startsWith(`${sectionPath}/`))
    ) {
      return path
    }

    return relativePath
  }, [absolutePath, path, relativePath, sectionContext?.path])
  const rootRelativeTargetPath = useMemo(() => {
    if (!targetPath || targetPath === '/') {
      return targetPath
    }

    return `//${targetPath.slice(1)}` as Path
  }, [targetPath])
  const commitHandleRef = useRef<() => void>(undefined)
  const switchContainerModeRef =
    useRef<(mode: ContainerMode) => void>(undefined)
  const containerModeRef = useRef<ContainerMode>(undefined)
  const {
    value: entries = [],
    moveValueToPath,
    getValueByPath,
  } = useDataValue<Array<unknown>>(rootRelativeTargetPath)

  const { setNextContainerMode } = useSwitchContainerMode(targetPath)
  const { hasReachedLimit, setShowStatus } = useArrayLimit(targetPath)
  const cancelHandler = useCallback(() => {
    if (hasReachedLimit) {
      setShowStatus(false)
    }
  }, [hasReachedLimit, setShowStatus])

  const showOpenButton = showOpenButtonWhen?.(entries)
  const newItemContextProps: PushContainerContext = {
    path,
    itemPath,
    entries,
    commitHandleRef,
    switchContainerMode: switchContainerModeRef.current,
  }

  const data = useMemo(() => {
    if (defaultDataProp) {
      return undefined // don't return a fallback, because we want to use the defaultData
    }
    return {
      ...isolatedData,
      pushContainerItems: [Object.freeze(dataProp) ?? clearedData],
    }
  }, [dataProp, defaultDataProp, isolatedData])

  if (outerData) {
    // Use assign to avoid mutating the original data object.
    // Because changes from outside should only silently be applied to the
    // data object, without triggering a rerender.
    // This way "pushContainerItems" will not clear/unset changed data.
    if (!Object.isFrozen(data)) {
      Object.assign(data || {}, outerData)
    }
  }

  const defaultData = useMemo(() => {
    return {
      ...(!dataProp ? isolatedData : null),
      pushContainerItems: [Object.freeze(defaultDataProp ?? clearedData)],
    }
  }, [dataProp, defaultDataProp, isolatedData])

  const emptyData = useCallback(
    (data: { pushContainerItems: unknown[] }) => {
      const firstItem = data.pushContainerItems?.[0]
      if (firstItem === null || typeof firstItem !== 'object') {
        return {
          ...isolatedData,
          pushContainerItems: [null],
        }
      }

      return {
        ...isolatedData,
        pushContainerItems: [dataProp ?? defaultDataProp ?? clearedData],
      }
    },
    [dataProp, defaultDataProp, isolatedData]
  )

  // Derive an isolation-specific schema from parent context schema
  // so that fields inside PushContainer validate against the target array's item schema.
  const isolationSchema = useMemo(() => {
    const parentSchema = outerContext?.props?.schema as unknown

    if (!parentSchema || !targetPath) {
      return undefined // stop here
    }

    if (isZodSchema(parentSchema)) {
      // Extract array element subschema: e.g. "/entries/0" → item schema
      const element = extractZodSubSchema(
        parentSchema as unknown as z.ZodTypeAny,
        `${targetPath}/0`
      ) as z.ZodTypeAny
      return z.object({ pushContainerItems: z.array(element) })
    } else {
      // JSON Schema: find items schema at the target path
      // Convert e.g. "/entries" → "/properties/entries/items"
      const segments = String(targetPath).split('/').filter(Boolean)
      const schemaPointer = `/properties/${segments.join(
        '/properties/'
      )}/items`
      const itemsSchema = pointer.has(parentSchema, schemaPointer)
        ? pointer.get(parentSchema, schemaPointer)
        : undefined

      if (!itemsSchema) {
        return undefined // stop here
      }

      return {
        type: 'object',
        properties: {
          pushContainerItems: {
            type: 'array',
            items: itemsSchema,
          },
        },
      }
    }
  }, [outerContext?.props?.schema, targetPath])

  return (
    <Isolation
      data={data}
      defaultData={defaultData}
      required={required}
      dataReference={dataReference}
      emptyData={emptyData}
      bubbleValidation={
        containerModeRef.current === 'view' ? false : bubbleValidation
      }
      commitHandleRef={commitHandleRef}
      schema={isolationSchema}
      transformOnCommit={({ pushContainerItems }) => {
        return moveValueToPath(
          targetPath,
          typeof insertAt === 'number'
            ? [
                ...entries.slice(0, insertAt),
                ...pushContainerItems,
                ...entries.slice(insertAt),
              ]
            : [...entries, ...pushContainerItems],
          targetPath && targetPath !== '/'
            ? structuredClone(getValueByPath('/'))
            : {}
        )
      }}
      onCommit={(data, options) => {
        const { clearData, preventCommit } = options
        if (hasReachedLimit) {
          preventCommit()
          setShowStatus(true)
        } else {
          setNextContainerMode('view')
          switchContainerModeRef.current?.('view')
          clearData()
        }
        onCommit?.(data, options)
      }}
    >
      <PushContainerContext value={newItemContextProps}>
        <SectionContext
          value={
            sectionContext
              ? { ...sectionContext, path: undefined }
              : undefined
          }
        >
          <IterateArray
            path="/pushContainerItems"
            containerMode={showOpenButton ? 'view' : 'edit'}
            withoutFlex
            omitSectionPath
          >
            <NewContainer
              title={title}
              openButton={openButton}
              switchContainerModeRef={switchContainerModeRef}
              showOpenButton={showOpenButton}
              cancelHandler={cancelHandler}
              containerModeRef={containerModeRef}
              rerenderPushContainer={forceUpdate}
              preventUncommittedChanges={preventUncommittedChanges}
              showResetButton={showResetButton}
              outerContext={outerContext}
              required={required}
              {...rest}
            >
              {children}
            </NewContainer>
          </IterateArray>
        </SectionContext>
      </PushContainerContext>
    </Isolation>
  )
}

function NewContainer({
  title,
  openButton,
  showOpenButton,
  showResetButton,
  switchContainerModeRef,
  cancelHandler,
  containerModeRef,
  rerenderPushContainer,
  preventUncommittedChanges,
  outerContext,
  required,
  children,
  ...rest
}) {
  const iterateItemContext = useContext(IterateItemContext)
  const { containerMode, switchContainerMode } = iterateItemContext || {}
  containerModeRef.current = containerMode

  const { hasContentChanged, showStatus } = useHandleStatus({
    outerContext,
    preventUncommittedChanges,
    error: pushContainerError,
    name: 'push-container',
  })

  useEffect(() => {
    rerenderPushContainer()
  }, [containerMode, rerenderPushContainer])

  const visibilityContext = useContext(VisibilityContext)
  switchContainerModeRef.current = switchContainerMode
  const isVisible =
    visibilityContext?.isVisible === false
      ? false
      : Boolean(
          !showOpenButton ||
          containerMode === 'edit' ||
          ((required || hasContentChanged) && showStatus)
        )

  const { preventUncommittedChangesText } = useTranslation().Isolation
  const { createButton } = useTranslation().IteratePushContainer
  const { clearData } = useContext(DataContext) || {}
  const restoreOriginalValue = useCallback(() => {
    clearData?.()
  }, [clearData])

  const newItemContextProps = {
    ...iterateItemContext,
    restoreOriginalValue,
  }

  const toolbar = (
    <Toolbar>
      <IterateItemContext value={newItemContextProps}>
        <Flex.Horizontal gap="large">
          <DoneButton text={createButton} />
          {showOpenButton && <CancelButton onClick={cancelHandler} />}
          {(preventUncommittedChanges || showResetButton) && (
            <ResetButton
              // Use hidden in order to render the useHasContentChanged hook
              hidden={!(showResetButton || showStatus)}
            />
          )}
        </Flex.Horizontal>

        {preventUncommittedChanges && showStatus && (
          <FormStatus noAnimation={false} show={hasContentChanged}>
            {preventUncommittedChangesText}
          </FormStatus>
        )}
      </IterateItemContext>
    </Toolbar>
  )

  return (
    <VisibilityContext value={{ isVisible, keepInDOM: false }}>
      <EditContainer
        open={isVisible}
        title={title}
        toolbar={toolbar}
        {...rest}
        // Add the class by default, because we don't get a "hasSubmitError" trigger
        className={clsx('dnb-forms-section-block--error', rest.className)}
      >
        {children}
      </EditContainer>

      {openButton && typeof showOpenButton === 'boolean' && (
        <HeightAnimation open={showOpenButton && containerMode === 'view'}>
          {openButton}
        </HeightAnimation>
      )}
    </VisibilityContext>
  )
}

const pushContainerError = new Error('Iterate.PushContainer')

PushContainer.OpenButton = OpenButton
withComponentMarkers(PushContainer, {
  _supportsSpacingProps: true,
})

export default PushContainer
